// Coluna da direita: quem é a pessoa do outro lado.
//
// O que dá valor real ao módulo é esta coluna — sem ela, o Inbox é só um
// WhatsApp Web com outra cara. É aqui que a conversa encosta no cadastro:
// a UC do cliente e a negociação do CRM.
//
// O vínculo pode não existir: contato solto é o caso comum antes da Fase 15
// (vínculo por telefone com confirmação humana). Quando não há, a tela diz isso
// em vez de mostrar um espaço vazio.

import React, { useEffect, useState } from 'react';
import { Building2, ExternalLink, Handshake, Link2Off, Phone, Radio } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Chamado, ConexaoPublica, ConversaLista, TicketStatus } from '../tipos';
import type { Etiqueta, Membro } from '../apiOrganizacao';
import PainelChamado from './PainelChamado';
import PainelTrabalho from './PainelTrabalho';
import { carregarNegociacao, carregarUnidade, type VinculoNegociacao, type VinculoUC } from '../api';
import { desdeQuando, formatarTelefone, nomeExibicao } from '../formato';
import Avatar from './Avatar';
import SugestaoVinculo from './SugestaoVinculo';

interface Props {
  /** Chamado depois de vincular ou desvincular, para a conversa recarregar. */
  onVinculoMudou?: () => void;
  conversa: ConversaLista | null;
  conexao: ConexaoPublica | null;
  chamado: Chamado | null;
  ocupadoChamado: boolean;
  onMudarStatusChamado: (s: TicketStatus) => void;
  onEscalarChamado: () => void;
  /** Fase 14 — só chega preenchido quando a org do usuário foi resolvida. */
  orgId?: string | null;
  meuId?: string | null;
  membros?: Membro[];
  etiquetas?: Etiqueta[];
  etiquetasDaConversa?: string[];
  onTrabalhoMudou?: () => void;
}

const ROTULO_STATUS: Record<string, string> = {
  open: 'Aberta',
  pending: 'Aguardando',
  resolved: 'Resolvida',
};

export default function PainelContato({
  conversa, conexao, chamado, ocupadoChamado, onMudarStatusChamado, onEscalarChamado,
  orgId = null, meuId = null, membros = [], etiquetas = [], etiquetasDaConversa = [],
  onTrabalhoMudou,
  onVinculoMudou,
}: Props) {
  const [uc, setUc] = useState<VinculoUC | null>(null);
  const [negociacao, setNegociacao] = useState<VinculoNegociacao | null>(null);

  useEffect(() => {
    let vivo = true;
    setUc(null);
    setNegociacao(null);
    if (conversa?.unidade_consumidora_id) {
      carregarUnidade(conversa.unidade_consumidora_id).then((r) => vivo && setUc(r));
    }
    if (conversa?.negociacao_id) {
      carregarNegociacao(conversa.negociacao_id).then((r) => vivo && setNegociacao(r));
    }
    return () => { vivo = false; };
  }, [conversa?.unidade_consumidora_id, conversa?.negociacao_id]);

  if (!conversa) {
    return (
      <aside className="hidden min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 xl:flex" />
    );
  }

  const semVinculo = !conversa.unidade_consumidora_id && !conversa.negociacao_id;

  return (
    <aside className="hidden min-h-0 flex-col overflow-y-auto rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 xl:flex">
      <div className="flex flex-col items-center border-b border-gray-200 px-4 py-5 text-center dark:border-slate-700">
        <Avatar
          nome={conversa.contato_nome}
          telefone={conversa.contato_telefone}
          url={conversa.contato_avatar}
          chave={conversa.contact_id}
          tamanho="lg"
        />
        <h3 className="mt-2.5 text-sm font-bold text-gray-900 dark:text-white">
          {nomeExibicao(conversa.contato_nome, conversa.contato_telefone)}
        </h3>
        {conversa.contato_telefone && (
          <a
            href={`tel:${conversa.contato_telefone}`}
            className="mt-0.5 inline-flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-primary dark:text-slate-400"
          >
            <Phone size={11} />
            {formatarTelefone(conversa.contato_telefone)}
          </a>
        )}
        <span className="mt-2.5 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-slate-700 dark:text-slate-300">
          {ROTULO_STATUS[conversa.status] ?? conversa.status}
          {conversa.responsavel_nome ? ` · ${conversa.responsavel_nome}` : ' · sem responsável'}
        </span>
      </div>

      {/* Fase 14 — responsável, situação, etiquetas e notas. Fica ACIMA do
          chamado e dos vínculos de propósito: é o que a pessoa mexe várias
          vezes por conversa, enquanto UC e negociação são leitura. */}
      {orgId && meuId && (
        <PainelTrabalho
          conversa={conversa}
          orgId={orgId}
          meuId={meuId}
          membros={membros}
          etiquetas={etiquetas}
          etiquetasDaConversa={etiquetasDaConversa}
          onMudou={() => onTrabalhoMudou?.()}
        />
      )}

      {chamado && (
        <PainelChamado
          chamado={chamado}
          ocupado={ocupadoChamado}
          onMudarStatus={onMudarStatusChamado}
          onEscalar={onEscalarChamado}
        />
      )}

      <div className="border-b border-gray-200 px-4 py-3.5 dark:border-slate-700">
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
          No sistema
        </h4>

        {semVinculo && (
          <div className="flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-slate-900/50">
            <Link2Off size={14} className="mt-0.5 shrink-0 text-gray-400 dark:text-slate-500" />
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Este contato ainda não está ligado a nenhum cliente ou negociação.
            </p>
          </div>
        )}

        {conversa.unidade_consumidora_id && (
          <Link
            to="/financeiro/ucs"
            className="group mb-2 flex items-start gap-2.5 rounded-xl border border-gray-200 px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-primary/5 dark:border-slate-700 dark:hover:border-primary/40"
          >
            <Building2 size={15} className="mt-0.5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500">
                Unidade consumidora
              </p>
              <p className="truncate text-xs font-semibold text-gray-800 dark:text-slate-200">
                {uc?.nome_cliente ?? `UC #${conversa.unidade_consumidora_id}`}
              </p>
              {uc?.cidade && (
                <p className="truncate text-[11px] text-gray-500 dark:text-slate-400">{uc.cidade}</p>
              )}
            </div>
            <ExternalLink size={12} className="mt-0.5 shrink-0 text-gray-300 transition-colors group-hover:text-primary dark:text-slate-600" />
          </Link>
        )}

        {conversa.negociacao_id && (
          <Link
            to={`/crm?negociacaoId=${conversa.negociacao_id}`}
            className="group flex items-start gap-2.5 rounded-xl border border-gray-200 px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-primary/5 dark:border-slate-700 dark:hover:border-primary/40"
          >
            <Handshake size={15} className="mt-0.5 shrink-0 text-secondary" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500">
                Negociação
              </p>
              <p className="truncate text-xs font-semibold text-gray-800 dark:text-slate-200">
                {negociacao?.nome_cliente ?? `#${conversa.negociacao_id}`}
              </p>
              {negociacao?.etapa && (
                <p className="truncate text-[11px] text-gray-500 dark:text-slate-400">{negociacao.etapa}</p>
              )}
            </div>
            <ExternalLink size={12} className="mt-0.5 shrink-0 text-gray-300 transition-colors group-hover:text-primary dark:text-slate-600" />
          </Link>
        )}
      </div>

      {/* Vem DEPOIS do vínculo já gravado e ANTES do canal: quem abre o painel
          quer primeiro saber de quem é a conversa, e só depois por onde ela
          chegou. */}
      <SugestaoVinculo
        contactId={conversa.contact_id}
        unidadeAtual={conversa.unidade_consumidora_id}
        negociacaoAtual={conversa.negociacao_id}
        onVinculado={onVinculoMudou ?? (() => {})}
      />

      {conexao && (
        <div className="border-t border-gray-200 px-4 py-3.5 dark:border-slate-700">
          <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
            Canal
          </h4>
          <div className="flex items-start gap-2.5">
            <Radio
              size={14}
              className={`mt-0.5 shrink-0 ${conexao.status === 'active' ? 'text-emerald-500' : 'text-red-500'}`}
            />
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-gray-800 dark:text-slate-200">
                {conexao.display_name}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-slate-400">
                {conexao.status === 'active' ? 'conectado' : conexao.status} · sinal {desdeQuando(conexao.last_seen_at)}
              </p>
              {conexao.capabilities?.isUnofficial && (
                <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                  Canal não oficial: o número pode ser banido sem aviso.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
